import React, { useState, useEffect } from 'react';
import api from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import Toast from '../components/Toast';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [garduList, setGarduList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ nama: '', username: '', password: '', role: 'petugas', ultg_id: 1, gardu_induk_id: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ nama: '', role: 'petugas', ultg_id: 1, gardu_induk_id: '', no_hp: '' });
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');

  const [resetUser, setResetUser] = useState(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [isResetSubmitting, setIsResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('semua');
  const [filterStatus, setFilterStatus] = useState('semua');

  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [usersRes, garduRes] = await Promise.all([
        api.get('/users'),
        api.get('/gardu'),
      ]);
      setUsers(usersRes.data.data || []);
      setGarduList(garduRes.data.data || []);
    } catch (err) { console.error(err); }
    setIsLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      await api.post('/users', form);
      setShowForm(false);
      setForm({ nama: '', username: '', password: '', role: 'petugas', ultg_id: 1, gardu_induk_id: '' });
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal tambah user');
    }
    setIsSubmitting(false);
  };

  const toggleAktif = (id, aktif, nama) => {
    const aksi = aktif ? 'menonaktifkan' : 'mengaktifkan';
    setConfirmDialog({
      open: true,
      title: aktif ? 'Nonaktifkan User' : 'Aktifkan User',
      message: `Yakin ingin ${aksi} user "${nama}"?`,
      danger: aktif,
      onConfirm: () => executeToggleAktif(id, aktif, aksi),
    });
  };

  const executeToggleAktif = async (id, aktif, aksi) => {
    setConfirmDialog({ open: false });
    try {
      if (aktif) {
        await api.delete(`/users/${id}`);
      } else {
        await api.put(`/users/${id}/aktifkan`);
      }
      setToast({ message: `User berhasil di${aksi === 'menonaktifkan' ? 'nonaktifkan' : 'aktifkan'}`, type: 'success' });
      loadData();
    } catch (err) {
      const pesan = err.response?.data?.message || `Gagal ${aksi} user`;
      setToast({ message: pesan, type: 'error' });
      console.error(err);
    }
  };

  const openEdit = (user) => {
    setEditUser(user);
    setEditForm({
      nama: user.nama || '',
      role: user.role || 'petugas',
      ultg_id: user.ultg_id || 1,
      gardu_induk_id: user.gardu_induk_id || '',
      no_hp: user.no_hp || '',
    });
    setEditError('');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsEditSubmitting(true);
    setEditError('');
    try {
      await api.put(`/users/${editUser.id}`, { ...editForm, aktif: editUser.aktif });
      setEditUser(null);
      loadData();
    } catch (err) {
      setEditError(err.response?.data?.message || 'Gagal update user');
    }
    setIsEditSubmitting(false);
  };

  const openReset = (user) => {
    setResetUser(user);
    setResetPasswordValue('');
    setResetError('');
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (resetPasswordValue.length < 6) {
      setResetError('Password minimal 6 karakter');
      return;
    }
    setIsResetSubmitting(true);
    setResetError('');
    try {
      await api.put(`/users/${resetUser.id}/reset-password`, { password_baru: resetPasswordValue });
      setToast({ message: `Password untuk ${resetUser.nama} berhasil direset`, type: 'success' });
      setResetUser(null);
    } catch (err) {
      setResetError(err.response?.data?.message || 'Gagal reset password');
    }
    setIsResetSubmitting(false);
  };

  const filteredUsers = users.filter(u => {
    const q = searchQuery.trim().toLowerCase();
    const matchSearch = !q || u.nama?.toLowerCase().includes(q) || u.username?.toLowerCase().includes(q);
    const matchRole = filterRole === 'semua' || u.role === filterRole;
    const matchStatus = filterStatus === 'semua' || (filterStatus === 'aktif' ? u.aktif : !u.aktif);
    return matchSearch && matchRole && matchStatus;
  });

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-700';
      case 'supervisor': return 'bg-blue-100 text-blue-700';
      case 'manajemen': return 'bg-orange-100 text-orange-700';
      default: return 'bg-green-100 text-green-700';
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Manajemen User</h1>
        <button onClick={() => setShowForm(!showForm)}
          className="bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors">
          {showForm ? '✕ Batal' : '+ Tambah User'}
        </button>
      </div>

      {/* Form tambah user */}
      {showForm && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
          <h2 className="font-bold text-gray-800 mb-4">Tambah User Baru</h2>
          {error && <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nama Lengkap</label>
                <input type="text" value={form.nama} onChange={(e) => setForm({...form, nama: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Nama lengkap" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <input type="text" value={form.username} onChange={(e) => setForm({...form, username: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Username" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Password" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select value={form.role} onChange={(e) => setForm({...form, role: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="petugas">Petugas</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="manajemen">Manajemen</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              {form.role === 'petugas' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gardu Induk</label>
                  <select value={form.gardu_induk_id} onChange={(e) => setForm({...form, gardu_induk_id: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                    <option value="">Pilih GI</option>
                    {garduList.map(gi => <option key={gi.id} value={gi.id}>{gi.nama}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="mt-4 flex gap-3">
              <button type="submit" disabled={isSubmitting}
                className="px-6 py-2 bg-green-700 text-white rounded-lg hover:bg-green-600 disabled:opacity-50">
                {isSubmitting ? 'Menyimpan...' : 'Simpan User'}
              </button>
              <button type="button" onClick={() => setShowForm(false)}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                Batal
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tabel User */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Cari</label>
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Nama atau username..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Role</label>
            <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
              <option value="semua">Semua Role</option>
              <option value="admin">Admin</option>
              <option value="supervisor">Supervisor</option>
              <option value="manajemen">Manajemen</option>
              <option value="petugas">Petugas</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
              <option value="semua">Semua Status</option>
              <option value="aktif">Aktif</option>
              <option value="nonaktif">Nonaktif</option>
            </select>
          </div>
          {(searchQuery || filterRole !== 'semua' || filterStatus !== 'semua') && (
            <button onClick={() => { setSearchQuery(''); setFilterRole('semua'); setFilterStatus('semua'); }}
              className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 underline">
              Reset filter
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-4 border-b border-gray-100">
          <h2 className="font-bold text-gray-800">
            Daftar User ({filteredUsers.length}{filteredUsers.length !== users.length ? ` dari ${users.length}` : ''})
          </h2>
        </div>
        {isLoading ? (
          <div className="flex justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nama</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gardu Induk</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map((user, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">{user.nama}</td>
                    <td className="px-6 py-4 text-gray-600">{user.username}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${getRoleBadge(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{user.gardu_induk || '-'}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${user.aktif ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {user.aktif ? 'Aktif' : 'Nonaktif'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => openEdit(user)} title="Edit user"
                          className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 flex items-center justify-center transition-colors">
                          &#9998;
                        </button>
                        <button onClick={() => openReset(user)} title="Reset password"
                          className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 flex items-center justify-center transition-colors">
                          &#128273;
                        </button>
                        <button onClick={() => toggleAktif(user.id, user.aktif, user.nama)}
                          title={user.aktif ? 'Nonaktifkan user' : 'Aktifkan user'}
                          className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${user.aktif ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}>
                          {user.aktif ? '\u26D4' : '\u2713'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editUser && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-6 z-50" onClick={() => setEditUser(null)}>
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h2 className="font-bold text-gray-800 mb-4">Edit User: {editUser.nama}</h2>
            {editError && <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm">{editError}</div>}
            <form onSubmit={handleEditSubmit}>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nama Lengkap</label>
                  <input type="text" value={editForm.nama} onChange={(e) => setEditForm({...editForm, nama: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select value={editForm.role} onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                    <option value="petugas">Petugas</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="manajemen">Manajemen</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                {editForm.role === 'petugas' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gardu Induk</label>
                    <select value={editForm.gardu_induk_id} onChange={(e) => setEditForm({...editForm, gardu_induk_id: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option value="">Pilih GI</option>
                      {garduList.map(gi => <option key={gi.id} value={gi.id}>{gi.nama}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">No. HP</label>
                  <input type="text" value={editForm.no_hp} onChange={(e) => setEditForm({...editForm, no_hp: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
              </div>
              <div className="mt-5 flex gap-3">
                <button type="submit" disabled={isEditSubmitting}
                  className="px-6 py-2 bg-green-700 text-white rounded-lg hover:bg-green-600 disabled:opacity-50">
                  {isEditSubmitting ? 'Menyimpan...' : 'Simpan Perubahan'}
                </button>
                <button type="button" onClick={() => setEditUser(null)}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                  Batal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resetUser && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-6 z-50" onClick={() => setResetUser(null)}>
          <div className="bg-white rounded-xl w-full max-w-sm p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h2 className="font-bold text-gray-800 mb-4">Reset Password: {resetUser.nama}</h2>
            {resetError && <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm">{resetError}</div>}
            <form onSubmit={handleResetSubmit}>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password Baru</label>
              <input type="password" value={resetPasswordValue} onChange={(e) => setResetPasswordValue(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Minimal 6 karakter" required />
              <div className="mt-5 flex gap-3">
                <button type="submit" disabled={isResetSubmitting}
                  className="px-6 py-2 bg-green-700 text-white rounded-lg hover:bg-green-600 disabled:opacity-50">
                  {isResetSubmitting ? 'Menyimpan...' : 'Reset Password'}
                </button>
                <button type="button" onClick={() => setResetUser(null)}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                  Batal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        danger={confirmDialog.danger}
        confirmLabel={confirmDialog.danger ? 'Nonaktifkan' : 'Aktifkan'}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ open: false })}
      />
      <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
}
